import { NavigateFunction } from "react-router-dom"
import ApiHandler from "src/api/ApiHandler"

/**
 * Navigation service for class components to use React Router navigation
 * without needing to be wrapped in hooks.
 */
class NavigationService {
    private navigate: NavigateFunction | null = null

    setNavigate(navigate: NavigateFunction) {
        this.navigate = navigate
    }

    navigateTo(path: string, replace: boolean = false) {
        if (this.navigate) {
            this.navigate(path, { replace })
        } else {
            // Fallback to window.location if navigate not set (shouldn't happen in normal flow)
            window.location.href = path
        }
    }

    navigateToHome() {
        this.navigateTo("/")
    }

    navigateToLogin() {
        this.navigateTo("/login")
    }

    navigateToContact() {
        this.navigateTo("/contact")
    }

    navigateToTerms() {
        this.navigateTo("/terms")
    }

    navigateToSavedValuations() {
        this.navigateTo("/saved-valuations")
    }

    navigateToSellMyHome(address?: string) {
        let path = "/sell-my-home"
        if (address) {
            path += `?address=${encodeURIComponent(address)}`
        }
        this.navigateTo(path)
    }

    navigateToGetValuation(address?: string) {
        let path = "/get-valuation"
        if (address) {
            path += `?address=${encodeURIComponent(address)}`
        }
        this.navigateTo(path)
    }

    navigateToArea(areaId: string, areaSlug?: string) {
        let path = `/area/${encodeURIComponent(areaId)}`
        if (areaSlug) {
            path += `/${encodeURIComponent(areaSlug)}`
        }
        this.navigateTo(path)
    }

    async navigateToValuation(propertyId: string, apiHandler: ApiHandler, houseCondition?: string, removedHomes?: string) {
        try {
            const addressResponse = await apiHandler.getAddress({ "id": propertyId })
            const addressSlug = addressResponse?.aurl || addressResponse?.address_url || ""
            
            let path = `/valuate/${propertyId}`
            if (addressSlug) {
                path += `/${encodeURIComponent(addressSlug)}`
            }
            
            const queryParams = new URLSearchParams()
            if (houseCondition) {
                queryParams.set("house_condition", houseCondition)
            }
            if (removedHomes) {
                queryParams.set("removed_homes", removedHomes)
            }
            
            const queryString = queryParams.toString()
            if (queryString) {
                path += `?${queryString}`
            }
            
            this.navigateTo(path)
        } catch (error) {
            console.error("Error navigating to valuation:", error)
            // Fallback to route without slug
            this.navigateTo(`/valuate/${propertyId}`)
        }
    }
}

// Singleton instance
const navigationService = new NavigationService()
export default navigationService

